import _ from 'lodash';
import { EditOutlined } from '@ant-design/icons';
import { Button, Divider, Row, Typography } from 'antd';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import {
  dialogFunction,
  FormAddButton,
  FormCancelButton,
  FormDeleteButton,
  FormEditButton,
} from 'common/components';
import { useCertificateColumns } from './hook';

import { EmptyView } from '../companyContact/AssociateLocationsModal';
import CustomTable from '../components/CustomTable';
import CreateCertificateModal from './CreateCertificateModal';

export const DEFAULT_STATE = { visible: false, mode: 'add', data: {} };

const CertificateTab = ({
  mode,
  dataSource,
  addData,
  editData,
  handleDeleteDataSource,
}) => {
  const refCertificateTable = useRef();

  const [actionState, setActionState] = useState({
    ...DEFAULT_STATE,
  });

  const onSubmit = (values) => {
    if (actionState.mode === 'add') addData(values);
    if (actionState.mode === 'edit') {
      editData({ ...values, key: actionState?.data?.key });
      refCertificateTable?.current?.resetValues();
    }
  };

  return (
    <>
      <Row>
        {mode === 'add' && (
          <FormAddButton
            text='Add Certificate'
            type='primary'
            onClick={() => {
              setActionState((pre) => ({ ...pre, visible: true }));
            }}
            style={{ marginLeft: 'auto' }}
          />
        )}
      </Row>
      <Divider style={{ marginBottom: 8, marginTop: 16 }} />
      {dataSource?.length > 0 && (
        <CertificateTable
          ref={refCertificateTable}
          mode={mode}
          title='Certificate'
          dataSource={dataSource}
          setActionState={setActionState}
          handleDeleteDataSource={handleDeleteDataSource}
        />
      )}
      {dataSource?.length === 0 && (
        <EmptyView
          style={{ marginTop: 200 }}
          text='No certificate have been created yet. Please create a certificate.'
        />
      )}

      {actionState.visible && (
        <CreateCertificateModal
          actionState={actionState}
          onSubmit={onSubmit}
          setActionState={setActionState}
        />
      )}
    </>
  );
};

const CertificateTable = forwardRef((props, ref) => {
  const { mode, dataSource, title, setActionState, handleDeleteDataSource } =
    props;

  const [selectedRowKeys, setSelectedRowKeys] = useState();
  const [currentItem, setCurrentItem] = useState();
  const [isEdit, setIsEdit] = useState(false);

  const hiddenFields = ['actions', 'expirationDate', 'associateLocationIds'];

  const { columns } = useCertificateColumns(hiddenFields);

  const handleDelete = () => {
    dialogFunction({
      type: 'warn',
      content: `Are you sure you want to delete the selected certificate?`,
      okText: 'Ok',
      okButtonProps: {
        type: 'primary',
      },
      onOk: () => {
        handleDeleteDataSource(selectedRowKeys[0]);
        resetValues();
      },
    });
  };

  const onClickEdit = () => {
    setActionState({
      visible: true,
      mode: 'edit',
      data: currentItem,
    });
  };

  useImperativeHandle(ref, () => {
    return {
      resetValues,
    };
  });

  const resetValues = () => {
    setSelectedRowKeys([]);
    setCurrentItem({});
  };

  const onCancel = () => {
    setIsEdit(false);
    resetValues();
  };

  return (
    <>
      <Row justify='space-between'>
        <Typography.Paragraph strong style={{ marginBottom: 12 }}>
          {title}
        </Typography.Paragraph>
        {!isEdit && (
          <Button
            type='link'
            icon={<EditOutlined />}
            onClick={() => setIsEdit(true)}
          />
        )}
        {isEdit && (
          <div>
            <FormEditButton
              icon={<EditOutlined />}
              onClick={onClickEdit}
              style={{ marginRight: 8 }}
              disabled={selectedRowKeys?.length !== 1}
              text='Edit'
            />
            {mode === 'add' && (
              <FormDeleteButton
                text='Delete'
                style={{ marginRight: 8 }}
                disabled={selectedRowKeys?.length !== 1}
                onClick={handleDelete}
              />
            )}
            <FormCancelButton
              text='Cancel'
              style={{ marginRight: 8, borderRadius: '4px' }}
              onClick={() => onCancel()}
            />
          </div>
        )}
      </Row>
      <div style={{ height: 500 }}>
        <CustomTable
          columns={columns}
          dataSource={dataSource}
          onSelectItem={(item) => {
            setCurrentItem(item);
          }}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
        />
      </div>
    </>
  );
});

export default CertificateTab;
